import "./styles/oddschecker-plus.module.css?__remix_sideEffect__";

import Headers from '@mjackson/headers';
import {
  json,
  type LinksFunction,
  type LoaderFunctionArgs } from
'@remix-run/node';
import { Outlet, useLoaderData, useRouteLoaderData } from '@remix-run/react';

import { REPUBS } from '@cx-modules/constants';

import { getFooterNav } from '~/api/footer-nav-api/footer-nav-api.service';
import { getUserContext } from '~/api/user-context/user-context.server';
import Footer from '~/components/BettingInsights/components/Footer';
import { Header } from '~/components/OddscheckerPlus/components/Header';
import ODDS_FORMAT from '~/enums/odds_format';
import { useAction } from '~/hooks/use-action';
import { BettingInsightsConfigProvider } from '~/providers/betting-insights-config-provider';
import {
  featureFlags,
  useFeatureFlag } from
'~/providers/feature-flag-provider';
import { LoaderProvider } from '~/providers/loader-context';
import ModalProvider from '~/providers/modal-context';
import { MyOCProvider } from '~/providers/myoc-context';
import { ServicesProvider } from '~/providers/services-context';
import { type loader as rootLoader } from '~/root-loader';
import stylesheet from '~/tailwind.css';
import { type MyOCData } from '~/types/myoc';
import { type RequestContext } from '~/types/RequestContext';
import { getRepubFromURL } from '~/utils/getRepubFromURL';
import { getMyOCConfig } from '~/utils/myoc.server';

export const links: LinksFunction = () => [
{
  rel: 'stylesheet',
  href: 'https://cdn.jsdelivr.net/npm/instantsearch.css@7/themes/satellite-min.css'
},
{ rel: 'stylesheet', href: stylesheet }];


export const loader = async ({ context, request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const repub = getRepubFromURL(url);
  const headers = new Headers(request.headers);
  const oddsType = headers.cookie.get('odds_type');

  const isLandingPage = new RegExp(
    `^${repub === REPUBS.OC.name ? '' : `/${repub.toLowerCase()}`}/plus/?$`
  ).test(url.pathname);

  const footerData = await getFooterNav.call(context, repub);
  const myocConfig = await getMyOCConfig.call(context, request);
  const myocData = await getUserContext.call(
    context,
    repub,
    myocConfig.deviceID,
    myocConfig.token
  );

  let oddsFormat;
  if (repub === REPUBS.US.name) {
    oddsFormat = ODDS_FORMAT.AMERICAN;
  } else {
    switch (oddsType) {
      case 'decimal':
        oddsFormat = ODDS_FORMAT.DECIMAL;
        break;
      case 'fractional':
      case 'traditional':
        oddsFormat = ODDS_FORMAT.FRACTIONAL;
        break;
      default:
        oddsFormat = ODDS_FORMAT.FRACTIONAL;
    }
  }

  return json({
    footerData,
    isLandingPage,
    myocConfig,
    myocData,
    oddsFormat,
    subscriptions: myocData.subscriptions,
    repub,
    url
  });
};

export default function OddscheckerPlus() {
  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const {
    ENV: { BETSLIP_SCRIPT_URL, OC_API_KEY },
    baseUrl,
    baseUrlWithProtocolAndRepub,
    countryCode,
    currentPath,
    currentPathWithRepub,
    deviceType,
    labels,
    myocConfig,
    myocData,
    repub,
    requestContext,
    resourcesUrl,
    siteConfig,
    state,
    url: currentUrl,
    user
  } = rootLoaderData;
  const { footerData, isLandingPage, oddsFormat, subscriptions } =
  useLoaderData<typeof loader>()!;

  const { submitAction } = useAction();
  const isTipstersEnabled = useFeatureFlag(featureFlags.SUBS__PLUS__TIPSTERS);

  return (
    <LoaderProvider
      apiResponse={undefined}
      baseUrl={baseUrl}
      baseUrlWithProtocolAndRepub={baseUrlWithProtocolAndRepub}
      currentPath={currentPath}
      currentPathWithRepub={currentPathWithRepub}
      deviceType={deviceType}
      repub={repub}
      state={state}
      user={user}>

			<BettingInsightsConfigProvider context={requestContext as RequestContext}>
				<MyOCProvider
          myocConfig={myocConfig}
          myocData={myocData as MyOCData}
          submit={submitAction}>

					<ModalProvider showMyOC={myocConfig.showMyOC}>
						<ServicesProvider
              NODE_ENV={ENV.NODE_ENV}
              countryCode={countryCode}
              labels={labels}
              repub={repub}
              siteConfig={siteConfig}
              state={state}>

							<Header
                BETSLIP_SCRIPT_URL={BETSLIP_SCRIPT_URL}
                apiKey={OC_API_KEY[repub]}
                baseUrl={baseUrl}
                countryCode={countryCode}
                currentUrl={currentUrl}
                isTipstersEnabled={isTipstersEnabled}
                oddsFormat={oddsFormat}
                repub={repub}
                resourcesUrl={resourcesUrl}
                subscriptions={subscriptions}
                user={user} />

							<Outlet context={{ oddsFormat }} />
							<Footer
                footerData={footerData}
                isLandingPage={isLandingPage}
                isTipstersEnabled={isTipstersEnabled}
                repub={repub}
                resourcesUrl={resourcesUrl} />

						</ServicesProvider>
					</ModalProvider>
				</MyOCProvider>
			</BettingInsightsConfigProvider>
		</LoaderProvider>);

}